"use strict";

class DataLayer {
  constructor(data = {}) {
    this.data = data;
    this.headerMenuLinks = null;
    this.selectors = {
      headerMenuLinks: ".header .header__menu .nav-link",
      headerMenuNavOpenedLink: ".header .header__menu .nav .nav-link.is-open",
      headerMenuSubmenu: ".sub-menu__item",
      headerMenuSubmenuLink: ".sub-menu__item .nav-link",
      searchInputs: "input[type='search']",
      newsletterForm: ".newsletter-form",
      logoutButtons: ".header-logout, .account .logout",
      banners: ".banner, .promo",
      promotions: [
        {
          element: ".top-notification",
          text: ".notification-text",
        },
        {
          element: ".promo",
          text: ".promo-text",
        },
      ],
    };
    this.dataLayer = window.dataLayer || [];

    if (!Object.keys(data).length) return;

    this.initData(this.data);
    this.getElements();
    this.bind();
    this.newsletterForm();
    this.loginOrRegister();
    this.logOut();
    this.bannersClick();
    this.outboundBannersClick();
    this.getAllErrors();
    this.promotionBanners();
    this.onSearchElementsClick();
    this.headerLinks();
    this.footerLinks();
  }

  initData(data) {
    if (data.site) {
      data.site.mediaquery = this.getCurrentBreakpoint();
    }
    if (data.page) {
      this.page = data.page;
    }

    this.dataLayer.push(this.data);
  }

  getElements() {
    this.headerMenuLinks = document.querySelectorAll(
      this.selectors.headerMenuLinks,
    );
    this.allSearchInputs = document.querySelectorAll(
      this.selectors.searchInputs,
    );
    this.allSearchForms = Array.from(this.allSearchInputs).map(
      (input) => input.form,
    );
  }

  bind() {
    this.allSearchForms.forEach((form) => {
      form?.addEventListener("submit", () => this.onSearchSubmit(form));
    });

    this.headerMenuLinks.forEach((link) => {
      link?.addEventListener("click", (e) => this.handleNavigationUse(e));
    });
  }

  resetEcommerce() {
    this.dataLayer.push({ ecommerce: null });
  }

  getCurrentBreakpoint() {
    const width = window.innerWidth;
    return width <= 600 ? "small" : width <= 960 ? "medium" : "large";
  }

  onSearchSubmit(form) {
    let searchQuery = form.querySelector(this.selectors.searchInputs)?.value;

    this.dataLayer.push({
      event: "search_use",
      search_action: "search",
      search_query: searchQuery,
    });
  }

  onSearchElementsClick() {
    document.addEventListener("predictive-search-rendered", () => {
      const searchLinks = document.querySelectorAll(
        ".predictive-search a[href]",
      );

      searchLinks.forEach((link) => {
        link.addEventListener("click", () => {
          let url = link.href;

          if (!url) return;

          url = new URL(link.href);

          if (link.closest("#predictive-search-results-products-list")) {
            this.dataLayer.push({
              event: "search_use",
              action: "PDP",
              search_query: url.pathname + url.search,
            });
          } else {
            this.dataLayer.push({
              event: "search_use",
              search_action: "search",
              search_query: url.pathname + url.search,
            });
          }
        });
      });
    });
  }

  handleNavigationUse(e) {
    const hierarchy = this.getNavigationHierarchy(e.target);
    this.dataLayer.push({
      event: "navigation_use",
      navigation_text: hierarchy.text,
      navigation_level: hierarchy.level,
    });
  }

  getNavigationHierarchy(element) {
    let level = 0;
    let array = [];
    let baseNavLinkText = "";
    let currentElement = element.closest(this.selectors.headerMenuSubmenu);
    let navOpenedLink = document.querySelector(
      this.selectors.headerMenuNavOpenedLink,
    );

    while (
      currentElement &&
      !currentElement.classList.contains("header__menu")
    ) {
      if (
        currentElement.classList.contains(
          this.selectors.headerMenuSubmenu.replace(".", ""),
        )
      ) {
        level++;
        const linkText = currentElement
          .querySelector(this.selectors.headerMenuSubmenuLink)
          ?.textContent.trim();
        array.unshift(linkText);
      }
      currentElement = currentElement.parentNode;
    }

    if (navOpenedLink) {
      level++;
      const linkText = navOpenedLink?.textContent.trim();
      array.unshift(linkText);
    }

    if (!currentElement) {
      baseNavLinkText = element?.textContent.trim();
    }

    return {
      text: array.length ? array.join(" -> ") : baseNavLinkText,
      level,
    };
  }

  newsletterForm() {
    const newsletterForms = document.querySelectorAll(
      this.selectors.newsletterForm,
    );

    if (!newsletterForms.length) return;

    newsletterForms.forEach((form) => {
      let formId = form.getAttribute("data-layer");
      let email = form.querySelector("input[type='email']");

      if (!form.getAttribute("data-layer")) return;

      form.addEventListener(
        "invalid",
        (event) => {
          const target = event.target;

          this.dataLayer.push({
            event: "validation_message",
            validation_text: target.validationMessage,
            validation_field: target.name || target.id,
          });
        },
        true,
      );

      form.addEventListener("submit", () => {
        this.dataLayer.push({
          event: "newsletter_subscription",
          formId: formId,
          email: email?.value,
        });
      });
    });
  }

  logOut() {
    const logoutButtons = document.querySelectorAll(
      this.selectors.logoutButtons,
    );

    if (!logoutButtons.length) return;

    logoutButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.dataLayer.push({
          event: "logout",
          account_id: this.data.user.account_id,
        });
      });
    });
  }

  bannersClick() {
    const banners = document.querySelectorAll(this.selectors.banners);

    banners.forEach((banner) => {
      banner.addEventListener("click", (e) => {
        let classList = e.target?.className.replaceAll(" ", ".");
        this.dataLayer.push({
          event: "banner_click",
          banner_location: this.page,
          click_type: `${e.target?.tagName?.toLowerCase()}.${classList}`,
        });
      });
    });
  }

  outboundBannersClick() {
    let links = document.querySelectorAll("a[href][target=_blank]");

    links.forEach((link) => {
      link.addEventListener("click", () => {
        this.dataLayer.push({
          event: "outbound_banner_click",
          banner_location: this.page,
          click_type: link.tagName.toLowerCase(),
        });
      });
    });
  }

  getAllErrors() {
    const errors = document.querySelectorAll(".errors");
    const invalidInputs = document.querySelectorAll("[aria-invalid='true']");
    let errorMessages = [];

    if (!errors.length) return;

    invalidInputs.forEach((input) => {
      let label = input?.nextElementSibling?.innerText;

      label ? errorMessages.push(label) : false;
    });

    errors.forEach((error) => {
      let data = {
        event: "validation_message",
        validation_text: error.innerText,
        validation_field: errorMessages.join(", "),
      };

      this.dataLayer.push(data);
    });
  }

  promotionBanners() {
    this.selectors.promotions.forEach((promotions) => {
      const promotionElements = document.querySelectorAll(promotions.element);

      promotionElements.forEach((promotion) => {
        const promotionText = promotion.querySelector(promotions.text);

        if (!promotionText) return;

        this.getPromotionData(promotionText);

        promotion.addEventListener("click", () => {
          this.getPromotionData(promotionText);
        });
      });
    });
  }

  getPromotionData(promotionText) {
    const promotionId = promotionText.getAttribute("data-id");
    const text = promotionText.innerText
      .replace(/\n+/g, " ")
      .replace(" %", "%")
      .replace("  ", " ");

    this.resetEcommerce();
    this.dataLayer.push({
      event: "view_promotion",
      ecommerce: {
        promotion_id: promotionId,
        promotion_name: text,
      },
    });
  }

  loginOrRegister() {
    const links = document.querySelectorAll("a[href]:not(.nav-link)");

    links.forEach((link) => {
      link.addEventListener("click", () => {
        if (link.href?.includes("customer_authentication")) {
          this.dataLayer.push({
            event: "login",
            account_id: this.data?.user?.account_id ?? "guest",
            account_type: "regular",
          });
        }
      });
    });
  }

  headerLinks() {
    const headerLinks = document.querySelectorAll(
      "header a[href]:not(.nav-link)",
    );

    const linkTextMap = {
      cart: "Cart",
      customer_authentication: "Login",
      account: "My Account",
      wishlist: "My Wishlist",
    };

    headerLinks.forEach((link) => {
      link.addEventListener("click", () => {
        let linkText = link.textContent?.trim() || "Logo";

        if (
          linkText === "Logo" &&
          link.querySelector("img")?.src?.includes("logo")
        ) {
          linkText = "Logo";
        }

        Object.keys(linkTextMap).forEach((keyword) => {
          if (link.href?.includes(keyword)) {
            linkText = linkTextMap[keyword];
          }
        });

        this.dataLayer.push({
          event: "header_click",
          navigation_text: linkText,
        });
      });
    });
  }

  footerLinks() {
    const footerLinks = document.querySelectorAll(".footer a");

    footerLinks.forEach((link) => {
      link.addEventListener("click", () => {
        this.dataLayer.push({
          event: "footer_click",
          navigation_text: link.textContent.trim(),
        });
      });
    });
  }

  socialLinks() {
    const socialLinks = document.querySelectorAll(".list-social__item a");

    socialLinks.forEach((link) => {
      link.addEventListener("click", () => {
        this.dataLayer.push({
          event: "social_click",
          navigation_text: link.textContent.trim(),
        });
      });
    });
  }
}
